import { WalletReadyState } from "@solana/wallet-adapter-base";
import type { Adapter } from "@solana/wallet-adapter-base";
import type { Wallet } from "@solana/wallet-adapter-react";
import {
  CoinbaseWalletAdapter,
  PhantomWalletAdapter,
  SolflareWalletAdapter,
} from "@solana/wallet-adapter-wallets";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { t } from "i18next";
import Image from "next/image";
import { useCallback, useMemo, useState } from "react";
import type { FC } from "react";
import { HiOutlineArrowNarrowLeft } from "react-icons/hi";
import {
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleFill,
} from "react-icons/ri";

import { useLocalStorageUserSettings } from "hooks/useLocalStorageUserSettings";
import { fetchCountryCode } from "services/marinade/fetchCountryCode";
import { Button } from "tw/components/atoms/Button";
import { Markdown } from "tw/components/atoms/Markdown";
import { getWalletIcon } from "utils/get-wallet-icon";

export interface WalletConnectBodyProps {
  wallets: Wallet[];
  initMode?: WalletConnectMode;
  requestConnection: (wallet: Wallet) => void;
}

export enum WalletConnectMode {
  WalletSelection,
  NoWallet,
  TermsAgreement,
}

export const WalletConnectBody: FC<WalletConnectBodyProps> = ({
  wallets,
  initMode,
  requestConnection,
}) => {
  const [walletConnectMode, setWalletConnectMode] = useState<WalletConnectMode>(
    initMode ?? WalletConnectMode.WalletSelection
  );
  const [selectedWallet, setSelectedWallet] = useState<Wallet | undefined>(
    undefined
  );

  const { data: countryCode } = useQuery(
    ["countryCode"],
    () => fetchCountryCode(),
    {
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const [
    savedRecentWallet,
    saveRecentWallet,
    termsAgreement,
    setTermsAgreement,
    jurisdictionConsent,
    setJurisdictionConsent,
    expertUserConsent,
    setExpertUserConsent,
  ] = useLocalStorageUserSettings();

  const { recentWallet, otherWallets } = useMemo(() => {
    return {
      recentWallet: wallets.find((w) => w.adapter.name === savedRecentWallet),
      otherWallets: wallets
        .filter(
          (w) =>
            w.adapter.name !== savedRecentWallet &&
            w.readyState !== WalletReadyState.Unsupported
        )
        .sort((a, b) => {
          if (a.readyState === b.readyState) {
            return 0;
          }
          return a.readyState === WalletReadyState.Installed ? -1 : 1;
        }),
    };
  }, [savedRecentWallet, wallets]);

  const walletButtonFromWalletAdapter = (
    adapter: Adapter,
    showGetButton: boolean,
    onClick: () => void
  ) => {
    const showIconBg =
      adapter.name.toLowerCase() === "xdefi" ||
      adapter.name.toLowerCase() === "mathwallet";
    return (
      <button
        key={`wallet_download_name_${adapter.name}`}
        type="button"
        className="flex gap-4 py-2 items-center w-full"
        onClick={onClick}
      >
        <Image
          src={adapter.icon}
          alt={`${adapter.name} wallet icon`}
          width={48}
          height={48}
          style={{ objectFit: "contain" }}
          className={`${
            showIconBg
              ? "p-1 bg-black rounded-full w-12 h-12"
              : "bg-white rounded-none w-12 h-12"
          }`}
        />
        <span className="w-full font-semibold text-lg flex justify-between">
          <span className="text-primary-text">{adapter.name}</span>
          {showGetButton && (
            <span className="ml-auto mr-0 px-5 h-8 text-lg text-primary border-primary border rounded-full">
              {t("walletConnect.no-wallet.get-button")}
            </span>
          )}
        </span>
      </button>
    );
  };

  const walletButtonFromWallet = useCallback(
    (wallet: Wallet) => {
      return walletButtonFromWalletAdapter(wallet.adapter, false, () => {
        if (wallet.adapter.readyState === WalletReadyState.NotDetected) {
          window.open(wallet.adapter.url, "_blank", "noreferrer");
        } else if (
          !termsAgreement ||
          !jurisdictionConsent ||
          (!expertUserConsent && countryCode === "GB")
        ) {
          setSelectedWallet(wallet);
          setWalletConnectMode(WalletConnectMode.TermsAgreement);
        } else {
          saveRecentWallet(wallet.adapter.name);
          requestConnection(wallet);
        }
      });
    },
    [
      termsAgreement,
      jurisdictionConsent,
      expertUserConsent,
      countryCode,
      requestConnection,
      saveRecentWallet,
    ]
  );

  const noWalletState = useMemo(() => {
    const coinbase = new CoinbaseWalletAdapter();
    const solflare = new SolflareWalletAdapter();
    const phantom = new PhantomWalletAdapter();
    return (
      <div className="relative overflow-y-scroll">
        <motion.button
          className="flex gap-1 items-center text-primary font-semibold mb-[18px]"
          onClick={() => {
            setWalletConnectMode(WalletConnectMode.WalletSelection);
          }}
        >
          <HiOutlineArrowNarrowLeft size={24} />
          {t("walletConnect.no-wallet.back")}
        </motion.button>
        <div className="h-full">
          {walletButtonFromWalletAdapter(solflare, true, () => {
            window.open(solflare.url, "_blank", "noreferrer");
          })}
          {walletButtonFromWalletAdapter(phantom, true, () => {
            window.open(phantom.url, "_blank", "noreferrer");
          })}
          {walletButtonFromWalletAdapter(coinbase, true, () => {
            window.open(coinbase.url, "_blank", "noreferrer");
          })}
          <span className="pt-6 flex flex-col gap-2 text-base text-center pb-6">
            <span className="w-full font-semibold text-primary-text">
              {t("walletConnect.no-wallet.subtitle")}
            </span>
            <span className="w-full font-normal text-tertiary-text">
              {t("walletConnect.no-wallet.desc")}
            </span>
          </span>
        </div>
      </div>
    );
  }, []);

  const termsWalletState = useMemo(() => {
    const walletIcon = (name: string | null | undefined) =>
      getWalletIcon(wallets.find((w) => w.adapter.name === name)?.adapter.name);
    return (
      <div className="relative overflow-y-scroll">
        <button
          type="button"
          className="flex gap-1 items-center text-primary font-semibold mb-[18px]"
          onClick={() => {
            setWalletConnectMode(WalletConnectMode.WalletSelection);
          }}
        >
          <HiOutlineArrowNarrowLeft size={24} />
          {t("walletConnect.consent.back")}
        </button>
        <span className="flex text-sm py-2">
          <button
            type="button"
            className="w-9 h-9 gap-x-1.5 p-1.5"
            onClick={() => {
              setTermsAgreement(!termsAgreement);
            }}
          >
            {termsAgreement ? (
              <RiCheckboxCircleFill className="text-primary" size={24} />
            ) : (
              <RiCheckboxBlankCircleLine
                className="text-additional-text"
                size={24}
              />
            )}
          </button>
          <Markdown
            className="prose text-sm"
            text={t("walletConnect.consent.terms")}
          />
        </span>
        <span className="flex text-sm py-2">
          <button
            type="button"
            className="w-9 h-9 gap-x-1.5 p-1.5"
            onClick={() => {
              setJurisdictionConsent(!jurisdictionConsent);
            }}
          >
            {jurisdictionConsent ? (
              <RiCheckboxCircleFill className="text-primary" size={24} />
            ) : (
              <RiCheckboxBlankCircleLine
                className="text-additional-text"
                size={24}
              />
            )}
          </button>
          {t("walletConnect.consent.jurisdiction")}
        </span>
        {countryCode === "GB" ? (
          <span className="flex text-sm py-2">
            <button
              type="button"
              className="w-9 h-9 gap-x-1.5 p-1.5"
              onClick={() => {
                setExpertUserConsent(!expertUserConsent);
              }}
            >
              {expertUserConsent ? (
                <RiCheckboxCircleFill className="text-primary" size={24} />
              ) : (
                <RiCheckboxBlankCircleLine
                  className="text-additional-text"
                  size={24}
                />
              )}
            </button>
            {t("walletConnect.consent.expert-user")}
          </span>
        ) : undefined}
        <Button
          variant="primary"
          size="large"
          label={t("walletConnect.consent.connect-button").replace(
            "{{wallet}}",
            selectedWallet?.adapter?.name ?? "Wallet"
          )}
          className="w-full h-12 mt-6 mb-5"
          disabled={
            !termsAgreement ||
            !jurisdictionConsent ||
            (!expertUserConsent && countryCode === "GB")
          }
          leftIcon={walletIcon(selectedWallet?.adapter.name)}
          leftIconSizePx={24}
          onClick={() => {
            if (!termsAgreement || !jurisdictionConsent) return;
            if (selectedWallet) {
              requestConnection(selectedWallet);
              saveRecentWallet(selectedWallet.adapter.name);
            }
          }}
        />
      </div>
    );
  }, [
    termsAgreement,
    jurisdictionConsent,
    expertUserConsent,
    selectedWallet,
    wallets,
    setTermsAgreement,
    setJurisdictionConsent,
    setExpertUserConsent,
    requestConnection,
    saveRecentWallet,
    countryCode,
  ]);

  const walletConnectState = useMemo(() => {
    const shouldShowDropShadow =
      (recentWallet ? 1 : 0) + otherWallets.length > 3;
    return (
      <>
        <div className="max-h-[310px] md:max-h-[380px] h-full w-full overflow-y-scroll">
          {recentWallet && (
            <>
              <div className="h-8 text-lg text-tertiary-text font-medium">
                {t("walletConnect.wallets.recent")}
              </div>
              {walletButtonFromWallet(recentWallet)}
            </>
          )}
          <div className="h-12 pt-4 text-lg text-tertiary-text font-medium pb-2">
            {t("walletConnect.wallets.popular")}
          </div>
          {otherWallets.map((w) => walletButtonFromWallet(w))}
          {shouldShowDropShadow && <div className="h-8" />}
        </div>
        <div
          className={`sticky bottom-0 bg-white ${
            shouldShowDropShadow
              ? "shadow-[0px_0px_50px_30px_rgba(255,255,255,1)]"
              : ""
          }`}
        >
          <motion.button
            whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
            className="pt-5 pb-6 text-primary font-semibold w-full content-center"
            onClick={() => {
              setWalletConnectMode(WalletConnectMode.NoWallet);
            }}
          >
            {t("walletConnect.no-wallet.prompt")}
          </motion.button>
        </div>
      </>
    );
  }, [otherWallets, recentWallet, walletButtonFromWallet]);

  return (
    <div className="h-full">
      {walletConnectMode === WalletConnectMode.WalletSelection &&
        walletConnectState}
      {walletConnectMode === WalletConnectMode.NoWallet && noWalletState}
      {walletConnectMode === WalletConnectMode.TermsAgreement &&
        termsWalletState}
    </div>
  );
};
